import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import { StarDisplay } from "./StarRating";
import { LevelBadge, BadgeList } from "./LevelBadge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Card, CardContent } from "./ui/card";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export const UserCard = ({ user, rank = null }) => {
  const { token } = useAuth();

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getPhotoUrl = () => {
    if (user.profile_photo && token) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/files/${user.profile_photo}?auth=${token}`;
    }
    return null;
  };

  const hasWarning = user.badges?.includes("warning");

  return (
    <Link to={`/user/${user.id}`} data-testid={`user-card-${user.id}`}>
      <Card className={`guardian-card cursor-pointer ${hasWarning ? 'border-red-200' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Rank Badge */}
            {rank && (
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-sm
                ${rank === 1 ? 'bg-amber-400 text-amber-900' : ''}
                ${rank === 2 ? 'bg-slate-300 text-slate-700' : ''}
                ${rank === 3 ? 'bg-amber-600 text-amber-100' : ''}
                ${rank > 3 ? 'bg-slate-100 text-slate-600' : ''}
              `} style={{ fontFamily: 'Outfit' }}>
                {rank}
              </div>
            )}

            {/* Avatar */}
            <Avatar className="h-14 w-14 border-2 border-white shadow-sm">
              <AvatarImage src={getPhotoUrl()} alt={user.full_name} />
              <AvatarFallback className="bg-[#FF3366] text-white text-lg font-bold">
                {getInitials(user.full_name)}
              </AvatarFallback>
            </Avatar>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-slate-900 truncate">{user.full_name}</h3>
                {user.is_verified && (
                  <ShieldCheck size={16} className="text-green-500 flex-shrink-0" />
                )}
                {hasWarning && (
                  <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <LevelBadge level={user.level} size="small" />
                <StarDisplay rating={user.average_rating} size={14} />
                <span className="text-xs text-slate-500">
                  ({user.total_ratings} avis)
                </span>
              </div>
              {user.badges && user.badges.length > 0 && !hasWarning && (
                <div className="mt-1">
                  <BadgeList badges={user.badges.filter(b => b !== 'warning')} size="small" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
